import { SafetyViolationTypes, type IProfession } from '@d-orca/shared/trusstor';
import type { IReplayEvent, IReplayFrame } from '@d-orca/shared/client/interfaces';
import {
  countByType,
  equipmentTypeRows,
  NO_FLOOR_LABEL,
  sortManagerRows,
  toManagerListRow,
  toWorkforceRow,
  tradeChip,
  type EquipmentTypeRow,
  type ManagerListRow,
  type ManagerRecord,
  type SafetyRow,
  type WorkforceRow,
} from '@store/selectors/monitor.selectors';
import { namedManagers, type ReplayEquipment } from './buildReplayView';

export type ReplaySafetyEvent = Extract<IReplayEvent, { kind: 'safety' }>;

/** The LeftRail at the playhead, in the live rows' shapes. */
export interface IReplayRail {
  professionRows: WorkforceRow[];
  managerRows: ManagerListRow[];
  equipmentRows: EquipmentTypeRow[];
  /** The day's safety alerts up to the playhead, newest first. */
  safetyRows: SafetyRow[];
}

/** No dash for a floor the live list doesn't carry: its id, as the building labels it. */
const floorLabel = (floorLabels: ReadonlyMap<string, string>, floorId: string) => floorLabels.get(floorId) ?? floorId;

/**
 * One row per profession on site in the frame (unfiltered, as live lists every
 * on-site trade), with its head count and floors; most workers first. A
 * profession the site's catalogue no longer has (deleted, or re-created under a
 * new id since that day) has no trade to show: it is left out, as the building
 * leaves out its chip (`aggregateTradeChips`).
 */
export function replayWorkforceRows(
  frame: IReplayFrame | null,
  professions: IProfession[],
  floorLabels: ReadonlyMap<string, string>,
): WorkforceRow[] {
  if (!frame) return [];
  const professionById = new Map(professions.map((profession) => [profession._id, profession]));
  const onSite = new Map<string, { profession: IProfession; count: number; floors: string[] }>();
  for (const [floorId, floor] of Object.entries(frame.floors)) {
    for (const [id, count] of Object.entries(floor.professions)) {
      const profession = professionById.get(id);
      if (count <= 0 || !profession?.tradeId) continue;
      const entry = onSite.get(id) ?? { profession, count: 0, floors: [] };
      entry.count += count;
      entry.floors.push(floorLabel(floorLabels, floorId));
      onSite.set(id, entry);
    }
  }
  return [...onSite.values()]
    .sort((a, b) => b.count - a.count || a.profession._id.localeCompare(b.profession._id))
    .map(({ profession, count, floors }) => toWorkforceRow(profession, profession, count, floors));
}

/**
 * The managers on site in the frame, as live's rows: matched to the site's
 * manager list by name (frames carry tag ids and names; the filter keys on `_id`),
 * so a row's checkbox is the same filter as live. One absent from the list keeps
 * its name and a neutral colour.
 */
export function replayManagerRows(frame: IReplayFrame | null, managers: readonly ManagerRecord[]): ManagerListRow[] {
  if (!frame) return [];
  const byName = new Map(managers.map((manager) => [manager.name, manager]));
  const rows = new Map<string, ManagerListRow>();
  for (const floor of Object.values(frame.floors)) {
    for (const { name } of namedManagers(floor)) {
      const row = toManagerListRow({ ...byName.get(name), name });
      rows.set(row.id, row);
    }
  }
  return sortManagerRows([...rows.values()]);
}

/** Equipment per type: located in the frame / seen that day (the day's dictionary). */
export function replayEquipmentRows(frame: IReplayFrame | null, equipment: ReplayEquipment): EquipmentTypeRow[] {
  const located = frame ? Object.values(frame.floors).flatMap((floor) => floor.equipmentIds) : [];
  return equipmentTypeRows(
    countByType(Object.values(equipment)),
    countByType(located.map((id) => ({ type: equipment[id]?.type }))),
  );
}

const VIOLATION_TYPES = new Set<string>(Object.values(SafetyViolationTypes));
const isViolationType = (typeKey: string): typeKey is SafetyViolationTypes => VIOLATION_TYPES.has(typeKey);

/**
 * The crossed safety alerts (in time order, as the replay index keeps them) as
 * live rows, newest first; the floor label from the building. An alert of a type
 * this build doesn't know is left out, as the live feed's schema drops it.
 */
export function replaySafetyRows(events: readonly ReplaySafetyEvent[], floorLabels: ReadonlyMap<string, string>): SafetyRow[] {
  return events
    .flatMap((event) =>
      isViolationType(event.typeKey)
        ? [
            {
              id: event.eventId,
              isCritical: event.isCritical,
              type: event.typeKey,
              timestamp: Date.parse(event.at),
              floorLabel: event.floorId ? floorLabel(floorLabels, event.floorId) : NO_FLOOR_LABEL,
              chip: event.trade && tradeChip(event.trade),
            },
          ]
        : [],
    )
    .reverse();
}
